<script lang="ts" setup>
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import type { Ref } from 'vue';
import type { OfferMedium } from '~/types/Catalog';
import { appendQueryParameters } from '~/utils/multicardSeo';

const {
  offers,
  title,
  noPadding,
  recommendationsBlockId,
  displayNameMode = 'default',
} = defineProps<{
  offers: OfferMedium[];
  title?: string | null;
  noPadding?: boolean;
  recommendationsBlockId?: string;
  displayNameMode?: 'default' | 'without-properties';
}>();

const existingOffers = computed(() => {
  return offers.filter((offer) => offer.id);
});
const selectedOffers = ref<Record<number, OfferMedium>>({});

function getCardOffer(offer: OfferMedium) {
  return selectedOffers.value[offer.id] ?? offer;
}

function setSelectedCardOffer(sourceOfferId: number, offer: OfferMedium) {
  selectedOffers.value[sourceOfferId] = offer;
}

function getOfferUrl(offer: OfferMedium) {
  if (!recommendationsBlockId) return offer.uri;

  return appendQueryParameters(
    offer.uri,
    new URLSearchParams({
      recommended_by: 'dynamic',
      recommended_code: recommendationsBlockId,
    }),
  );
}

const isShowNavigation = computed(() => existingOffers.value.length > 4);

onMounted(() => {
  sliderInitialization();
});

const swiperInstance: Ref<any> = ref();
const swiperBlock = ref();
const swiperPaginationBlock = ref();

function sliderInitialization() {
  swiperInstance.value = new Swiper(swiperBlock.value, {
    breakpoints: {
      640: {
        slidesOffsetBefore: noPadding ? 0 : 12,
        spaceBetween: 12,
      },
      1024: {
        slidesOffsetBefore: 0,
        spaceBetween: 16,
      },
    },
    modules: [Pagination, Navigation],
    navigation: {
      nextEl: '.swiper-button--next',
      prevEl: '.swiper-button--prev',
    },
    pagination: {
      clickable: true,
      el: swiperPaginationBlock.value,
      type: 'bullets',
    },
    slidesOffsetBefore: noPadding ? 0 : 12,
    slidesPerView: 'auto',
    spaceBetween: 8,
  });
}

const { sendOfferEventToDataLayer } = useGtmModule();
</script>

<template>
  <section
    :class="{ '!px-0': noPadding }"
    class="product-cards-gallery"
  >
    <div
      v-if="title"
      :class="{ 'px-3 lg:px-0': !noPadding }"
      v-html="title"
    />

    <div
      ref="swiperBlock"
      class="swiper product-cards-gallery__container"
    >
      <div class="swiper-wrapper">
        <AppLink
          v-for="(offer, key) in existingOffers"
          :key="offer.id"
          :to="getOfferUrl(getCardOffer(offer))"
          class="swiper-slide product-cards-gallery__item hover:text-black"
          @click="sendOfferEventToDataLayer('select_item', getCardOffer(offer), key)"
        >
          <ProductCard
            :display-name-mode="displayNameMode"
            :offer="getCardOffer(offer)"
            :recommendations-block-id="recommendationsBlockId"
            @select-offer="setSelectedCardOffer(offer.id, $event)"
          />
        </AppLink>
      </div>

      <div
        v-if="isShowNavigation"
        class="swiper-navigation"
      >
        <div class="swiper-button swiper-button--prev">
          <SvgoChevronLeft
            class="text-2xl"
            filled
          />
        </div>

        <div class="swiper-button swiper-button--next">
          <SvgoChevronRight
            class="text-2xl"
            filled
          />
        </div>
      </div>

      <div
        ref="swiperPaginationBlock"
        class="swiper-pagination"
      />
    </div>
  </section>
</template>

<style lang="scss">
.product-cards-gallery {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  &__container {
    width: 100%;
  }

  &__item {
    width: 100%;
    max-width: 46vw;
    height: auto;

    @include screen-sm {
      width: 218px;
    }
  }

  & .product-card-image__hover-pagination {
    @media screen and (width < 1024px) {
      display: none !important;
    }
  }
}
</style>
